/**
 * Membre connecté (rôle, profil terrain, préférences) et « espace » de travail : Assistant, Expert ou Administration.
 * Un assistant ne voit que l'espace Assistant, un expert l'espace Expert ; le compte administrateur arrive sur
 * l'espace Administration (équipe, réglages du cabinet) et peut consulter les deux autres.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type { Membre } from '@/lib/types';
import { useSession } from './useSession';

export type Espace = 'assistant' | 'expert' | 'admin';
const CLE_ESPACE = 'lx-espace';

export function lireEspace(): Espace | null {
  try {
    const v = localStorage.getItem(CLE_ESPACE);
    return v === 'assistant' || v === 'expert' || v === 'admin' ? v : null;
  } catch {
    return null;
  }
}
export function memoriserEspace(e: Espace) {
  try { localStorage.setItem(CLE_ESPACE, e); } catch { /* navigation privée */ }
}

export function useMembre() {
  const { session } = useSession();
  return useQuery({
    queryKey: ['membre', session?.user.id],
    enabled: !!session,
    queryFn: async () => {
      const { data, error } = await supabase.from('membres').select('*').eq('user_id', session!.user.id).maybeSingle();
      if (error) throw error;
      return data as Membre | null;
    },
  });
}

/** Espaces accessibles selon le rôle. */
export const espacesDu = (role: Membre['role'] | undefined): Espace[] =>
  role === 'assistant' ? ['assistant'] : role === 'expert' ? ['expert'] : ['assistant', 'expert', 'admin'];

export const LIBELLE_ESPACE: Record<Espace, string> = { assistant: 'Assistant', expert: 'Expert', admin: 'Administration' };

export function espaceCourant(role: Membre['role'] | undefined): Espace {
  const permis = espacesDu(role);
  const choisi = lireEspace();
  return choisi && permis.includes(choisi) ? choisi : permis[permis.length - 1];
}

/** Espace affiché : celui choisi via le bouton « Espace … » (ou l'URL /assistant, /expert), dans la limite du rôle. */
export function useEspace(): Espace {
  return espaceCourant(useMembre().data?.role);
}

export function useEquipe() {
  return useQuery({
    queryKey: ['equipe'],
    queryFn: async () => {
      const { data, error } = await supabase.from('membres').select('*').order('nom');
      if (error) throw error;
      return (data ?? []) as Membre[];
    },
  });
}

export function useMajMembre() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ user_id, patch }: { user_id: string; patch: Partial<Membre> }) => {
      const { error } = await supabase.from('membres').update(patch).eq('user_id', user_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['membre'] });
      qc.invalidateQueries({ queryKey: ['equipe'] });
      toast.success('Enregistré');
    },
    onError: (e) => toast.error((e as Error).message),
  });
}

/** Fiche tournée d'un expert (base, spécialités, RDV / jour, disponible) : assistanat ou administrateur. */
export function useMajFicheExpert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ user_id, patch }: { user_id: string; patch: Partial<Pick<Membre, 'adresse_base' | 'base_lat' | 'base_lng' | 'natures' | 'max_rdv_jour' | 'actif'>> }) => {
      const { error } = await supabase.rpc('maj_fiche_expert', { p_expert: user_id, p_patch: patch });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['membre'] });
      qc.invalidateQueries({ queryKey: ['equipe'] });
      toast.success('Fiche expert enregistrée');
    },
    onError: (e) => toast.error((e as Error).message),
  });
}

export const nomMembre = (m: Pick<Membre, 'prenom' | 'nom' | 'email'> | null | undefined) =>
  [m?.prenom, m?.nom].filter(Boolean).join(' ') || m?.email || '—';
